const EASTERN_TIME_ZONE = 'America/New_York';
const WINDOW_LENGTH_DAYS = 14;
const CLOSED_LENGTH_DAYS = 49;
const CYCLE_LENGTH_DAYS = WINDOW_LENGTH_DAYS + CLOSED_LENGTH_DAYS;

type LocalDate = { year: number; month: number; day: number };

export type IncubatorWindow = {
  isOpen: boolean;
  changesAt: Date;
  nextOpenAt: Date | null;
};

function addCalendarDays(date: LocalDate, days: number): LocalDate {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

function easternSevenPm(date: LocalDate): Date {
  const desiredAsUtc = Date.UTC(date.year, date.month - 1, date.day, 19);
  let instant = desiredAsUtc;

  // Resolve the Eastern offset at this date. A second pass handles DST accurately.
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: EASTERN_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(instant));
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value);
    const representedAsUtc = Date.UTC(
      value('year'),
      value('month') - 1,
      value('day'),
      value('hour'),
      value('minute')
    );
    instant += desiredAsUtc - representedAsUtc;
  }

  return new Date(instant);
}

/**
 * The current window closes July 17, 2026 at 7 PM ET. Thereafter,
 * applications are closed for seven weeks and open for two weeks.
 */
export function getIncubatorWindow(now = new Date()): IncubatorWindow {
  const anchor: LocalDate = { year: 2026, month: 7, day: 17 };
  const initialClose = easternSevenPm(anchor);

  if (now < initialClose) {
    return { isOpen: true, changesAt: initialClose, nextOpenAt: null };
  }

  for (let cycle = 1; cycle < 10_000; cycle += 1) {
    const closeDate = addCalendarDays(anchor, cycle * CYCLE_LENGTH_DAYS);
    const openDate = addCalendarDays(closeDate, -WINDOW_LENGTH_DAYS);
    const opensAt = easternSevenPm(openDate);
    const closesAt = easternSevenPm(closeDate);

    if (now < opensAt) {
      return { isOpen: false, changesAt: opensAt, nextOpenAt: opensAt };
    }
    if (now < closesAt) {
      return { isOpen: true, changesAt: closesAt, nextOpenAt: null };
    }
  }

  throw new Error('Unable to calculate the incubator application window.');
}

export function formatIncubatorDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}
